/*
  This file handles AJAX loading of products in a collection page.
  It initializes event listeners for "Load More" and "Collapse All" buttons,
  and manages the loading of additional products without a full page refresh.

*/

function initLoadMoreProducts() {
  // Run handleRefreshOnLastPage on page load
  handleRefreshOnLastPage();

  // Use event delegation on the document
  document.addEventListener('click', function (e) {
    const loadMoreBtn = e.target.closest('#loadMoreBtn');
    const collapsAllBtn = e.target.closest('#collapsAllBtn');
    const loadMoreWrapper = document.querySelector('.load-more-wrapper');
    const collapsAllWrapper = document.querySelector('.collaps-all-wrapper');

    // check for loadMoreBtn
    if (loadMoreBtn) {
      loadMoreProducts(loadMoreWrapper, loadMoreBtn);
      return; // Exit early to avoid checking collapsAllBtn if loadMoreBtn is clicked
    }

    // check for collapsAllBtn
    if (collapsAllBtn) {
      collapsAllProducts(collapsAllBtn, collapsAllWrapper, loadMoreBtn, loadMoreWrapper);
      return; // Exit early to avoid checking loadMoreBtn if collapsAllBtn is clicked
    }
  });
}

// Function to load more products via AJAX
async function loadMoreProducts(loadMoreWrapper, loadMoreBtn) {
  // UI: Disable button and show loader
  loadMoreBtn.disabled = true;
  const loader = loadMoreBtn.querySelector('.loader');
  if (loader) loader.classList.remove('hidden');

  try {
    // Get nextPageUrl
    const nextPageUrl = loadMoreBtn.dataset.nextPage; // e.g., "/collections/dubai?page=2"

    // Get currentPage
    const currentPage = getCurrentPage(nextPageUrl); // e.g., "2"

    // Get base pathname from nextPageUrl (e.g., "/collections/dubai")
    const basePath = new URL(nextPageUrl, window.location.origin).pathname;

    // Get all query parameters from current window URL
    const currentParams = new URLSearchParams(window.location.search);

    // Set/override the page param
    currentParams.set('page', currentPage);

    // find the params count in the currentParams
    const paramsCount = Array.from(currentParams.keys()).length;

    // If there are no additional parameters, we can use the nextPageUrl directly
    // Otherwise, we construct the new URL with basePath and currentParams
    let fetchUrl = nextPageUrl;
    if (paramsCount > 1) {
      // 4. Construct the new URL with basePath and currentParams
      fetchUrl = `${basePath}?${currentParams.toString()}`;
    }

    // Fetch the products using fetchUrl
    const response = await fetch(fetchUrl, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newProducts = doc.querySelector('#product-grid');
    const productGrid = document.querySelector('#product-grid');

    // Check if newProducts and productGrid exist
    if (!newProducts || !productGrid) throw new Error('Product grid not found.');

    // and directly append new products to the existing product grid
    productGrid.insertAdjacentHTML('beforeend', newProducts.innerHTML);

    // UI: Hide loader and re-enable button
    loadMoreBtn.disabled = false;
    if (loader) loader.classList.add('hidden');

    // Update next page URL or remove button if no more pages
    const nextUrl = doc.querySelector('#loadMoreBtn')?.dataset?.nextPage;
    if (nextUrl) {
      loadMoreBtn.dataset.nextPage = nextUrl;
    } else {
      loadMoreWrapper.classList.add('hidden');

      // show collapsAllWrapper
      const collapsAllWrapper = document.querySelector('.collaps-all-wrapper');
      if (collapsAllWrapper) collapsAllWrapper.classList.remove('hidden');
    }

    // Update browser URL (pushState) for SEO and navigation
    if (currentPage && paramsCount > 1) {
      history.pushState(null, '', `${basePath}?${currentParams.toString()}`);
    } else {
      history.pushState(null, '', `?page=${currentPage}`);
    }
  } catch (err) {
    // console.error('[Collection AJAX] Error loading more products:', err);

    // Hide loader and re-enable button on error
    loadMoreBtn.disabled = false;
    if (loader) loader.classList.add('hidden');
  }
}

// Function to collapse all products
async function collapsAllProducts(collapsAllBtn, collapsAllWrapper, loadMoreBtn, loadMoreWrapper) {
  // UI: Disable button and show loader
  collapsAllBtn.disabled = true;
  const loader = collapsAllBtn.querySelector('.loader');
  if (loader) loader.classList.remove('hidden');

  // Get base pathname from nextPageUrl (e.g., "/collections/dubai")
  const basePath = window.location.pathname;

  let additionalParams = '?sort_by=manual';

  // Get all query parameters from current window URL
  const currentParams = new URLSearchParams(window.location.search);
  if (currentParams.has('page')) {
    // If the page param exists, then remove it
    currentParams.delete('page');
  }

  // find the params count in the currentParams afte the page param is removed
  const paramsCount = Array.from(currentParams.keys()).length;

  // If ther are already params available in url then just replace them with params available in additionalParams
  if (paramsCount >= 1) {
    additionalParams = `?${currentParams.toString()}`;
  }

  // Construct the fetch URL
  const fetchUrl = `${basePath}${additionalParams}`;

  try {
    // Fetch the products using fetchUrl
    const response = await fetch(fetchUrl, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newProducts = doc.querySelector('#product-grid');
    const productGrid = document.querySelector('#product-grid');

    // Check if newProducts and productGrid exist
    if (!newProducts || !productGrid) throw new Error('Product grid not found.');

    // Clear the existing product grid and replace it with new products
    productGrid.innerHTML = newProducts.innerHTML;

    // UI: Hide loader and re-enable button
    collapsAllBtn.disabled = false;
    if (loader) loader.classList.add('hidden');

    // Update browser URL (pushState) for SEO and navigation
    history.pushState(null, '', fetchUrl);

    // Scroll to top of the product grid
    productGrid.scrollIntoView({ behavior: 'smooth' });

    // hide the collapsAllWrapper
    if (collapsAllWrapper) collapsAllWrapper.classList.add('hidden');

    // Show the loadMoreBtn if it exists
    if (loadMoreWrapper) {
      loadMoreWrapper.classList.remove('hidden');
      loadMoreBtn.dataset.nextPage = `${basePath}?page=2`; // Update nextPage URL as well
      loadMoreBtn.disabled = false; // Re-enable the button
    }
  } catch (err) {
    // Log the error to the console
    // console.error('[Collection AJAX] Error collapsing all products:', err);

    // Hide loader and re-enable button on error
    collapsAllBtn.disabled = false;
    if (loader) loader.classList.add('hidden');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLoadMoreProducts);
} else {
  initLoadMoreProducts(); // Otherwise, run immediately if already loaded
}

// function to get currentPage
function getCurrentPage(nextPageUrl) {
  // /collections/dubai?page=2
  const Url = new URL(nextPageUrl, window.location.origin);
  const currentPage = Url.searchParams.get('page') || null;
  return currentPage;
}

// function to handle refresh on last page on the pagination
function handleRefreshOnLastPage() {
  // Get nextUrl manually from the loadMoreBtn
  const nextUrl = document.querySelector('#loadMoreBtn')?.dataset?.nextPage;

  // if nextUrl exists, then return early because this means user is not on the last page
  if (nextUrl) return;

  // Get loadMoreWrapper
  const loadMoreWrapper = document.querySelector('.load-more-wrapper');

  // Get collapsAllWrapper
  const collapsAllWrapper = document.querySelector('.collaps-all-wrapper');

  // Check hidden class on loadMoreWrapper. If it does not have hidden class then add manually
  if (loadMoreWrapper && !loadMoreWrapper.classList.contains('hidden')) {
    loadMoreWrapper.classList.add('hidden');
  }

  // check if the collapsAllWrapper has hidden class. If yes then remove it manually
  if (collapsAllWrapper?.classList.contains('hidden')) {
    collapsAllWrapper.classList.remove('hidden');
  }
}
